<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="1000"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        style="max-width:36px !important"
        icon
        @click.stop=""
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Employee Profile"
      text="Update employee profile"
      class="mb-0"
      :loading="loading"
    > 
      <form v-if="dialog && !loading">
        <v-container
          data-simplebar
          style="max-height:650px"
        > 
          <v-row justify="center">
            <v-avatar size="130">
              <img
                :src="localPersonalDetail.thumbnail_url"
                onerror="this.src='img/thumbnail.jpg'"
              >
            </v-avatar>
          </v-row>

          <v-divider class="my-3" />

          <span class="font-weight-bold font-calibri font-18 black--text">Personal Info</span>
          <v-row>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localPersonalDetail.person.title_lookup_uid"
                v-validate="'required'"
                :class="{
                  'required': !localPersonalDetail.person.title_lookup_uid
                }"
                outlined
                :error-messages="errors.collect('title_lookup_uid')"
                data-vv-name="title_lookup_uid"     
                data-vv-as="title"
                :items="titles"
                item-text="value"
                item-value="lookup_uid"
                label="Title"
              />
            </v-col>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-text-field       
                v-model="localPersonalDetail.person.name"
                v-validate="'required|max:80|nameAllowedFormat'"
                :class="{
                  'required': !localPersonalDetail.person.name
                }"
                outlined
                label="Name"
                :error-messages="errors.collect('name')"
                data-vv-name="name"     
                data-vv-as="name"
                @input="localPersonalDetail.person.name = localPersonalDetail.person.name.toUpperCase()"
              />
            </v-col>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-text-field  
                v-model="localPersonalDetail.person.alias"          
                outlined
                label="Alias"
                @input="localPersonalDetail.person.alias = localPersonalDetail.person.alias.toUpperCase()"
              />
            </v-col>

            <v-col
              cols="4"
              class="pb-0"
            >
              <v-text-field
                v-model="localPersonalDetail.person.identity_card.ic_number"
                v-validate="localPersonalDetail.person.citizenship_lookup_uid == citizenship.find(e => e.value === 'MY').lookup_uid ? 'required|icDate|icLength' : ''"
                v-mask="localPersonalDetail.person.citizenship_lookup_uid == citizenship.find(e => e.value === 'MY').lookup_uid ? '######-##-####' : '################################'"
                outlined
                :class="{
                  'required': localPersonalDetail.person.citizenship_lookup_uid == citizenship.find(e => e.value === 'MY').lookup_uid && !localPersonalDetail.person.identity_card.ic_number
                }"
                :error-messages="errors.collect('ic_number')"  
                data-vv-name="ic_number"   
                data-vv-as="ic number"
                label="IC Number"
                :filled="localPersonalDetail.person.citizenship_lookup_uid != citizenship.find(e => e.value === 'MY').lookup_uid"
                :disabled="localPersonalDetail.person.citizenship_lookup_uid != citizenship.find(e => e.value === 'MY').lookup_uid"
                @input="getBirthdayFromIC(localPersonalDetail.person.identity_card.ic_number)"
              />
            </v-col>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-menu
                v-model="dobMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="dobDisplay"
                    v-validate="'required'"
                    :class="{
                      'required':!dobDisplay
                    }"
                    outlined
                    :error-messages="errors.collect('dobDisplay')"
                    data-vv-name="dobDisplay"  
                    data-vv-as="date of birth"
                    label="Date of Birth"
                    class="label-position"
                    v-on="on" 
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="localPersonalDetail.person.dob"
                  no-title
                  :max="today"
                  @input="dobMenu = false"
                />
              </v-menu> 
            </v-col>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localPersonalDetail.person.gender_lookup_uid"
                v-validate="'required'"
                :class="{
                  'required': !localPersonalDetail.person.gender_lookup_uid
                }"
                outlined
                :items="genders"
                item-text="value"
                item-value="lookup_uid"
                label="Gender"
                :error-messages="errors.collect('gender_lookup_uid')"  
                data-vv-name="gender_lookup_uid"   
                data-vv-as="gender"
              />
            </v-col>

            <v-col
              cols="4"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localPersonalDetail.person.citizenship_lookup_uid"
                v-validate="'required'"
                :class="{
                  'required': !localPersonalDetail.person.citizenship_lookup_uid
                }"
                outlined
                :error-messages="errors.collect('citizenship_lookup_uid')"  
                data-vv-name="citizenship_lookup_uid"   
                data-vv-as="citizenship"
                :items="citizenships"
                :item-text="item => '['+item.value+'] ' + item.description1"
                item-value="lookup_uid"
                label="Citizenship"
                @input="citizenshipChange(citizenships.find(citizen => {return citizen.lookup_uid === localPersonalDetail.person.citizenship_lookup_uid}))"
              />
            </v-col>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localPersonalDetail.person.religion_lookup_uid"
                v-validate="'required'"
                :class="{
                  'required': !localPersonalDetail.person.religion_lookup_uid
                }"
                outlined
                :items="religions"
                item-text="value"
                item-value="lookup_uid"
                label="Religion"
                :error-messages="errors.collect('religion_lookup_uid')"  
                data-vv-name="religion_lookup_uid"   
                data-vv-as="religion"
              />
            </v-col>
            <v-col
              cols="4"
              class="pb-0"
            >
              <span> Disabled? </span>
              <v-radio-group
                v-model="localPersonalDetail.is_disabled"
                class="mt-0"
                row
              >
                <v-radio
                  label="No"
                  :value="0"
                />
                <v-radio
                  label="Yes"
                  :value="1"
                /> 
              </v-radio-group>
            </v-col>

            <v-col
              cols="4"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localPersonalDetail.person.ethnicity_lookup_uid"
                v-validate="'required'"
                :class="{
                  'required': !localPersonalDetail.person.ethnicity_lookup_uid
                }"
                outlined
                :items="ethnicities"
                :item-text="item => item.type !== 'ethnicity' ? item.description2 : item.value"
                item-value="lookup_uid"
                label="Ethnicity"
                :error-messages="errors.collect('ethnicity_lookup_uid')"  
                data-vv-name="ethnicity_lookup_uid"   
                data-vv-as="ethnicity"
              />
            </v-col>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localPersonalDetail.person.dob_state_lookup_uid"
                v-validate="'required'" 
                :class="{
                  'required': !localPersonalDetail.person.dob_state_lookup_uid
                }"
                :items="placeOfBirths"
                :error-messages="errors.collect('dob_state_lookup_uid')"  
                data-vv-name="dob_state_lookup_uid"   
                data-vv-as="place of birth"
                :item-text="item => item.type !== 'place_of_birth' ? item.description1 : item.value"
                item-value="lookup_uid"
                outlined
                label="Place of Birth"
              />
            </v-col>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-text-field  
                v-model="localPersonalDetail.person.personal_email_address"
                v-validate="'email'"
                :error-messages="errors.collect('personalEmail')"
                data-vv-name="personalEmail"
                data-vv-as="personal email"
                label="Personal Email"
                outlined
              />
            </v-col>
            
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-text-field  
                v-model="localPersonalDetail.email"
                v-validate="'email'"
                :error-messages="errors.collect('email')"
                data-vv-name="email"
                data-vv-as="company email"
                label="Company Email"
                outlined
              />
            </v-col>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-text-field  
                v-model="localPersonalDetail.person.phone_number"
                v-validate="'phoneLength'"
                v-mask="['###-########']"
                :error-messages="errors.collect('phoneNumber')"
                data-vv-name="phoneNumber"
                label="Correspondence Phone Number"
                outlined
              />
            </v-col>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-text-field  
                v-model="localPersonalDetail.person.handphone_number"
                v-validate="'phoneLength'"
                v-mask="['###-########']"
                :error-messages="errors.collect('handphoneNumber')"
                data-vv-name="handphoneNumber"
                label="Correspondence Handphone Number"
                outlined
              />
            </v-col>

            <v-col
              cols="4"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localPersonalDetail.person.marital_status_lookup_uid"
                v-validate="'required'"
                :class="{
                  'required': !localPersonalDetail.person.marital_status_lookup_uid
                }"
                outlined
                :items="maritalStatuses"
                item-text="value"
                item-value="lookup_uid"
                label="Marital Status"
                :error-messages="errors.collect('marital_status_lookup_uid')"  
                data-vv-name="marital_status_lookup_uid"   
                data-vv-as="marital status"
              />
            </v-col>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-menu
                v-model="marriageDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="marriageDateDisplay"
                    v-validate=" localPersonalDetail.person.marital_status_lookup_uid=='1c661e3d-dbe1-11e7-ba92-509a4c2dfbad' ? 'required' : ''" 
                    outlined
                    :disabled="localPersonalDetail.person.marital_status_lookup_uid != '1c661e3d-dbe1-11e7-ba92-509a4c2dfbad'"
                    :filled="localPersonalDetail.person.marital_status_lookup_uid != '1c661e3d-dbe1-11e7-ba92-509a4c2dfbad'"
                    :error-messages="errors.collect('marriageDateDisplay')"
                    data-vv-name="marriageDateDisplay"
                    data-vv-as="marriage date"
                    readonly
                    label="Marriage Date"
                    class="label-position"
                    v-on="on" 
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="localPersonalDetail.person.marriage_date"
                  no-title
                  :max="today"
                  @input="marriageDateMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>

          <v-divider class="my-3" />
          <span class="font-weight-bold font-calibri font-18 black--text">Address</span>
          <v-row>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-text-field  
                v-model="localPersonalDetail.person.address_line_1"
                v-validate="'required'"
                :class="{
                  'required': !localPersonalDetail.person.address_line_1
                }"
                label="Address Line 1"
                :error-messages="errors.collect('address_line_1')"
                data-vv-name="address_line_1"
                outlined
                data-vv-as="address"
                @input="localPersonalDetail.person.address_line_1 = localPersonalDetail.person.address_line_1.toUpperCase()"
              />
            </v-col>

            <v-col
              cols="4"
              class="pb-0"
            >
              <v-text-field  
                v-model="localPersonalDetail.person.address_line_2"
                label="Address Line 2"
                outlined
                :disabled="!localPersonalDetail.person.address_line_1"
                @input="localPersonalDetail.person.address_line_2 = localPersonalDetail.person.address_line_2.toUpperCase()"
              />
            </v-col>

            <v-col
              cols="4"
              class="pb-0"
            >
              <v-text-field  
                v-model="localPersonalDetail.person.address_line_3"
                label="Address Line 3"
                outlined
                :disabled="!localPersonalDetail.person.address_line_1"
                @input="localPersonalDetail.person.address_line_3 = localPersonalDetail.person.address_line_3.toUpperCase()"
              />
            </v-col>

            <v-col
              cols="4"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localPersonalDetail.person.postcode"
                v-validate="'required'"
                :class="{
                  'required': !localPersonalDetail.person.postcode
                }"
                :items="postcodes"
                :disabled="!localPersonalDetail.person.address_line_1"
                item-text="postcode"
                item-value="postcode"
                value="postcode"
                :error-messages="errors.collect('postcode')"
                data-vv-name="postcode"
                data-vv-as="postcode" 
                label="Postcode"
                outlined
                @input="clearData(); postCodeOnChanged(localPersonalDetail.person.postcode)"
              />
            </v-col>

            <v-col
              cols="4"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localPersonalDetail.person.city_id"
                v-validate="'required'"
                :class="{
                  'required': !localPersonalDetail.person.city_id
                }"
                :disabled="dropdownDisabled"
                outlined
                :items="filteredCities"
                :error-messages="errors.collect('city_id')"
                data-vv-name="city_id"
                data-vv-as="city" 
                item-text="name"
                item-value="id"
                value="id"
                label="City"
                @input="cityOnChanged(localPersonalDetail.person.city_id)"
              />
            </v-col>

            <v-col
              cols="4"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localPersonalDetail.person.state_id"
                v-validate="'required'"
                :class="{
                  'required': !localPersonalDetail.person.state_id
                }"
                outlined
                :error-messages="errors.collect('state_id')"
                data-vv-name="state_id"
                data-vv-as="state"
                disabled
                :items="states"
                item-text="name"
                item-value="id"
                value="id"
                label="State"
              />
            </v-col>

            <v-col
              cols="4"
              class="pb-0"
            >
              <v-autocomplete
                v-model="localPersonalDetail.person.country_id"
                v-validate="'required'"
                :class="{
                  'required': !localPersonalDetail.person.country_id
                }"
                outlined
                :error-messages="errors.collect('country_id')"
                data-vv-name="country_id"
                data-vv-as="country"
                :items="countries"
                disabled
                item-text="name"
                item-value="id"
                value="id"
                label="Country"
              />
            </v-col>
            
            <v-col
              cols="4"
              class="pb-0"
            />
            
            <v-col
              cols="4"
              class="pb-0"
            />
          </v-row>

          <v-divider 
            v-show="localPersonalDetail.person.citizenship_lookup_uid != citizenship.find(e => e.value === 'MY').lookup_uid"
            class="my-3"
          />

          <!-- Work Permit -- ONLY ENABLED WHEN NEEDED -->
          <span 
            v-show="localPersonalDetail.person.citizenship_lookup_uid != citizenship.find(e => e.value === 'MY').lookup_uid"
            class="font-weight-bold font-calibri font-18 black--text"
          >
            Work Permit
          </span>
          <v-row
            v-show="localPersonalDetail.person.citizenship_lookup_uid != citizenship.find(e => e.value === 'MY').lookup_uid"
          >
            <v-col 
              cols="4"
              class="pb-0"
            >
              <v-text-field  
                v-model="localPersonalDetail.work_permit_number"
                v-validate="localPersonalDetail.person.citizenship_lookup_uid != citizenship.find(e => e.value === 'MY').lookup_uid ? 'required' : ''"
                :error-messages="errors.collect('workPermitNumber')"
                data-vv-name="workPermitNumber"
                data-vv-as="work permit number"
                :class="{
                  'required': !localPersonalDetail.work_permit_number
                }"
                label="Work Permit Number"
                outlined
              />
            </v-col>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-menu
                v-model="workPermitApprovedMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="workPermitApprovedDateDisplay"
                    v-validate="localPersonalDetail.person.citizenship_lookup_uid != citizenship.find(e => e.value === 'MY').lookup_uid ? 'required' : ''"
                    label="Work Permit Approved Date"
                    :error-messages="errors.collect('workPermitApprovedDateDisplay')" 
                    data-vv-name="workPermitApprovedDateDisplay"
                    data-vv-as="work permit approved date"
                    outlined
                    readonly
                    :class="{
                      'required': !localPersonalDetail.work_permit_approved_date
                    }"
                    class="label-position"
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template> 
                <v-date-picker
                  v-model="localPersonalDetail.work_permit_approved_date"
                  no-title
                  :max="today"
                  @input="workPermitApprovedMenu = false"
                />
              </v-menu>
            </v-col>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-menu
                v-model="workPermitExpiryMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="workPermitExpiryDateDisplay"
                    v-validate="localPersonalDetail.person.citizenship_lookup_uid != citizenship.find(e => e.value === 'MY').lookup_uid ? 'required' : ''"
                    label="Work Permit Expiry Date"
                    :error-messages="errors.collect('workPermitExpiryDateDisplay')"
                    data-vv-name="workPermitExpiryDateDisplay"
                    data-vv-as="work permit expiry date"
                    outlined
                    :class="{
                      'required': !localPersonalDetail.work_permit_expiry_date
                    }"
                    class="label-position" 
                    readonly
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="localPersonalDetail.work_permit_expiry_date"
                  no-title
                  :min="localPersonalDetail.work_permit_approved_date"
                  @input="workPermitExpiryMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>

          <v-divider 
            v-show="localPersonalDetail.person.citizenship_lookup_uid != citizenship.find(e => e.value === 'MY').lookup_uid"
            class="my-3"
          />
          <span 
            v-show="localPersonalDetail.person.citizenship_lookup_uid != citizenship.find(e => e.value === 'MY').lookup_uid"
            class="font-weight-bold font-calibri font-18 black--text"
          >
            Passport
            <employee-profile-general-info-personal-detail-dialog-list-identity-document-histories
              :identity-document-histories="localPersonalDetail.person.identity_card.histories"
              @updated="$emit('updated'); dialog = false"
            />
          </span>
          <v-row
            v-show="localPersonalDetail.person.citizenship_lookup_uid != citizenship.find(e => e.value === 'MY').lookup_uid"
          >
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-text-field   
                v-model="localPersonalDetail.person.identity_card.passport_number"
                v-validate="localPersonalDetail.person.citizenship_lookup_uid != citizenship.find(e => e.value === 'MY').lookup_uid ? 'required' : ''"
                :error-messages="errors.collect('passportNumber')"
                data-vv-name="passportNumber"
                data-vv-as="passport number"
                :class="{
                  'required': !localPersonalDetail.person.identity_card.passport_number
                }"
                outlined
                label="Passport Number"
              />
            </v-col>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-menu
                v-model="passportIssueDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="passportIssueDateDisplay"
                    v-validate="localPersonalDetail.person.citizenship_lookup_uid != citizenship.find(e => e.value === 'MY').lookup_uid ? 'required' : ''"
                    label="Passport Issue Date"
                    :error-messages="errors.collect('passportIssueDateDisplay')"
                    data-vv-name="passportIssueDateDisplay"
                    data-vv-as="passport issue date"
                    outlined
                    readonly
                    :class="{
                      'required': !localPersonalDetail.person.identity_card.passport_issue_date
                    }"
                    class="label-position"
                    v-on="on" 
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="localPersonalDetail.person.identity_card.passport_issue_date"
                  no-title
                  :max="today"
                  @input="passportIssueDateMenu = false"
                />
              </v-menu>
            </v-col>
            <v-col
              cols="4"
              class="pb-0"
            >
              <v-menu
                v-model="passportExpiryDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="passportExpiryDateDisplay"
                    v-validate="localPersonalDetail.person.citizenship_lookup_uid != citizenship.find(e => e.value === 'MY').lookup_uid ? 'required' : ''"
                    label="Passport Expiry Date"
                    :error-messages="errors.collect('passportExpiryDateDisplay')"
                    data-vv-name="passportExpiryDateDisplay"
                    data-vv-as="passport expiry date"
                    outlined
                    readonly
                    :class="{
                      'required':!localPersonalDetail.person.identity_card.passport_expiry_date
                    }"
                    class="label-position"
                    v-on="on" 
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="localPersonalDetail.person.identity_card.passport_expiry_date"
                  no-title
                  :min="localPersonalDetail.person.identity_card.passport_issue_date"
                  @input="passportExpiryDateMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>
        </v-container>
      </form>
      
      <v-card-actions v-if="dialog && !loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"    
          class="white--text px-10"           
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  import { mapState } from 'vuex'
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    components: {
      EmployeeProfileGeneralInfoPersonalDetailDialogListIdentityDocumentHistories: () => import('@/components/hris/employee/profile/general-info/personal-detail/DialogListIdentityDocumentHistories'),
    },
    props: {
      employeeUid: {
        type: String,
        required: true,
      },
      personalDetail: {
        type: Object,
        required: true,
      },
    },
    data () {
      return {
        progressing: false,
        dialog: null,
        loading: false,
        localPersonalDetail: null,
        countries: [],
        states: [],
        postcodes: [],
        cities: [],
        genders: [],
        ethnicities: [],
        titles: [],
        religions: [],
        maritalStatuses: [],
        placeOfBirths: [],
        citizenships: [],
        systemBasedCountry: 'MY',
        dobMenu: false,
        marriageDateMenu: false,
        workPermitApprovedMenu: false,
        workPermitExpiryMenu: false,
        passportIssueDateMenu: false,
        passportExpiryDateMenu: false,
        today: new Date().toISOString().slice(0, 10), 
        dropdownDisabled: null,
        cityIds: [],        
      }
    },
    computed: {
      ...mapState('lookup', ['citizenship']),

      dobDisplay () {
        return this.localPersonalDetail.person.dob ? this.$moment(this.localPersonalDetail.person.dob, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      marriageDateDisplay () {
        return this.localPersonalDetail.person.marriage_date && this.localPersonalDetail.person.marital_status_lookup_uid === '1c661e3d-dbe1-11e7-ba92-509a4c2dfbad' ? this.$moment(this.localPersonalDetail.person.marriage_date, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      workPermitApprovedDateDisplay () {
        return this.localPersonalDetail.work_permit_approved_date ? this.$moment(this.localPersonalDetail.work_permit_approved_date, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      workPermitExpiryDateDisplay () {
        return this.localPersonalDetail.work_permit_expiry_date ? this.$moment(this.localPersonalDetail.work_permit_expiry_date, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      passportIssueDateDisplay () {
        return this.localPersonalDetail.person.identity_card.passport_issue_date ? this.$moment(this.localPersonalDetail.person.identity_card.passport_issue_date, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      passportExpiryDateDisplay () {
        return this.localPersonalDetail.person.identity_card.passport_expiry_date ? this.$moment(this.localPersonalDetail.person.identity_card.passport_expiry_date, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      filteredCities () {
        if (this.localPersonalDetail.person.postcode) {
          this.postCodeOnChanged(this.localPersonalDetail.person.postcode)
        }
        
        if (this.cityIds.length > 0) {
          return this.cities.filter(city => {
            return this.cityIds.includes(city.id)
          })
        }
        return this.cities      
      },      
    },
    watch: {
      dialog: function (value) {
        if (!value) return 

        this.loading = true
        let scope = this

        this.$validator.extend('dependentsValue', {
          getMessage (field, args) {
            return 'Invalid Number of Dependents'
          },
          validate (value, args) {
            return value >= 0
          },
        })
        this.$validator.extend('icDate', {
          getMessage (field, args) {
            return 'Invalid IC Date'        
          },
          validate (value, args) {
            return scope.isValidDate(value) 
          },
        })
        this.$validator.extend('icLength', {
          getMessage (field, args) {
            return 'IC not in right length'        
          },
          validate (value, args) {
            return String(value).replace(/\D/g, '').length === 12
          },
        })
        this.$validator.extend('nameAllowedFormat', {
          getMessage (field, args) {
            return "Invalid name format. Only alphabet and special character of @ ' * - / . allowed for name"     
          },
          validate (value, args) {
            const regex = /^[ A-Za-z'@./*-]*$/
            return regex.test(value)
          },
        })
        this.$validator.extend('phoneLength', {
          getMessage (field, args) {
            return 'Invalid phone number (at least 9 numbers and max 11)'
          },
          validate (value, args) {
            return value.toString().length >= 10 && value.toString().length <= 12
          },
        })
        
        this.localPersonalDetail = { ...this.personalDetail }
        
        Promise.all([
          this.$api.countries().list(),
          this.$api.states().list(),
          this.$api.cities().list(),
          this.$api.postcodes().list(),
          this.$api.genders().list(),
          this.$api.ethnicities().list(),
          this.$api.citizenships().list(),
          this.$api.placeOfBirths().list(),
          this.$api.hrisPersonMaritalStatus().list(),
          this.$api.religions().list(),
          this.$api.titles().list(),
        ])
          .then(([countries, states, cities, postcodes, genders, ethnicities, citizenships, placeOfBirths, maritalStatus, religions, titles]) => {
            this.countries = countries.data
            this.states = states.data
            this.cities = cities.data
            this.postcodes = _.orderBy(postcodes.data, ['postcode'], ['asc'])
            this.genders = genders.data
            this.ethnicities = ethnicities.data
            this.citizenships = citizenships.data
            this.placeOfBirths = placeOfBirths.data
            this.maritalStatuses = maritalStatus.data
            this.religions = religions.data
            this.titles = titles.data

            if (this.localPersonalDetail.person.lookup_citizenship != null) {
              this.citizenshipChange(this.localPersonalDetail.person.lookup_citizenship)
            }

            this.loading = false
          })
      },
    },
    methods: { 
      prepareFormData () {
        return {
          title_lookup_uid: this.localPersonalDetail.person.title_lookup_uid,
          name: this.localPersonalDetail.person.name,
          alias: this.localPersonalDetail.person.alias,
          ic_number: (this.localPersonalDetail.person.identity_card.ic_number === null || (this.localPersonalDetail.person.citizenship_lookup_uid !== this.citizenship.find(e => e.value === 'MY').lookup_uid)) ? null : this.localPersonalDetail.person.identity_card.ic_number.replace(/-/g, ''),
          dob: this.localPersonalDetail.person.dob ? this.$moment(this.localPersonalDetail.person.dob).format('YYYY-MM-DD HH:mm:ss') : null,
          address_line_1: this.localPersonalDetail.person.address_line_1,
          address_line_2: this.localPersonalDetail.person.address_line_2,
          address_line_3: this.localPersonalDetail.person.address_line_3,
          postcode: this.localPersonalDetail.person.postcode,
          city_id: this.localPersonalDetail.person.city_id,
          state_id: this.localPersonalDetail.person.state_id,
          country_id: this.localPersonalDetail.person.country_id,
          marriage_date: this.localPersonalDetail.person.marital_status_lookup_uid === '1c661e3d-dbe1-11e7-ba92-509a4c2dfbad' ? this.localPersonalDetail.person.marriage_date : null,
          phone_number: this.localPersonalDetail.person.phone_number,
          handphone_number: this.localPersonalDetail.person.handphone_number,
          dob_state_lookup_uid: this.localPersonalDetail.person.dob_state_lookup_uid,
          gender_lookup_uid: this.localPersonalDetail.person.gender_lookup_uid,
          ethnicity_lookup_uid: this.localPersonalDetail.person.ethnicity_lookup_uid,
          citizenship_lookup_uid: this.localPersonalDetail.person.citizenship_lookup_uid,
          marital_status_lookup_uid: this.localPersonalDetail.person.marital_status_lookup_uid,
          religion_lookup_uid: this.localPersonalDetail.person.religion_lookup_uid,
          email: this.localPersonalDetail.email,
          personal_email_address: this.localPersonalDetail.person.personal_email_address,
          work_permit_number: this.localPersonalDetail.work_permit_number,
          work_permit_approved_date: this.localPersonalDetail.work_permit_approved_date,
          work_permit_expiry_date: this.localPersonalDetail.work_permit_expiry_date,
          passport_number: this.localPersonalDetail.person.identity_card.passport_number,
          passport_issue_date: this.localPersonalDetail.person.identity_card.passport_issue_date,
          passport_expiry_date: this.localPersonalDetail.person.identity_card.passport_expiry_date,
          is_disabled: this.localPersonalDetail.is_disabled,
        }
      },   
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 

            this.setProgressing(true)
    
            this.$api.personalDetails().update(this.employeeUid, this.prepareFormData())
              .then((response) => {
                if (response.status === 200 || response.status === 201) {
                  this.$emit('updated')                
                  this.$snack({ message: response.data.status })

                  this.setProgressing(false)
                  this.dialog = false       
                }
              })
          })
      },
      setProgressing (status) {
        this.progressing = status
      },
      clearData () {
        this.localPersonalDetail.person.city_id = null
        this.localPersonalDetail.person.state_id = null
        this.localPersonalDetail.person.country_id = null
      },
      postCodeOnChanged (postcodeValue) {
        var cityID = this.postcodes.filter(p => {
          return p.postcode === postcodeValue
        })  

        this.cityIds = this.cities.filter(city => {
          return cityID.some(city2 => {
            return city2.city_id === city.id
          })
        }).map(city3 => {
          return city3.id
        })

        if (this.cityIds.length > 1) {
          this.dropdownDisabled = false
        } else {
          this.dropdownDisabled = true

          let cityId = (this.postcodes.find(postcode => {
            return postcode.postcode === postcodeValue
          }) || {}).city_id

          if (cityId) {
            var cityObject = this.getCity(cityId)
            if (cityObject) {
              this.localPersonalDetail.person.city_id = cityObject.id
              var stateObject = this.getState(cityObject.state_id)
              
              if (stateObject) {
                this.localPersonalDetail.person.state_id = stateObject.id
                var countryObject = this.getCountry(stateObject.country_id)
                this.localPersonalDetail.person.country_id = countryObject.id
              }
            }
          }
        }
      },
      cityOnChanged (cityId) {
        var cityObject = this.getCity(cityId)
        if (cityObject) {
          var stateObject = this.getState(cityObject.state_id)
          
          if (stateObject) {
            this.localPersonalDetail.person.state_id = stateObject.id
            var countryObject = this.getCountry(stateObject.country_id)
            this.localPersonalDetail.person.country_id = countryObject.id
          }
        }
      },
      getCity (cityId) {
        if (isNaN(parseInt(cityId))) return null
        return this.cities.find(city => {
          return city.id === parseInt(cityId)
        })
      },
      getState (stateId) {
        if (isNaN(parseInt(stateId))) return null
        return this.states.find(state => {
          return state.id === parseInt(stateId)
        })
      },
      getCountry (countryId) {
        if (isNaN(parseInt(countryId))) return null
        return this.countries.find(country => {
          return country.id === parseInt(countryId)
        })
      },

      getBirthdayFromIC (value) {
        if (this.isValidDate(value)) {
          const currentYear = new Date().getFullYear()
          const century = Math.floor(currentYear / 100)
          const yearTwoDigits = value.slice(0, 2)
          const month = value.slice(2, 4)
          const day = value.slice(4, 6)

          let fullYear = `${century}${yearTwoDigits}`
          const formattedInput = `${fullYear}-${month}-${day}`

          if (formattedInput > this.today) {
            fullYear = String(Number(fullYear) - 100)
          }

          this.localPersonalDetail.person.dob = `${fullYear}-${month}-${day}`
          
          if (String(value).replace(/\D/g, '').length === 12) {
            this.localPersonalDetail.person.gender_lookup_uid = value.slice(-1) % 2 === 1 
              ? '1c661585-dbe1-11e7-ba92-509a4c2dfbad' // male
              : '1c66161e-dbe1-11e7-ba92-509a4c2dfbad' // female
          } else {
            this.localPersonalDetail.person.gender_lookup_uid = null
          }
        }
      },
      isValidDate (value) {
        var y = parseInt(value.substr(0, 2))
        var m = parseInt(value.substr(2, 2))
        var d = parseInt(value.substr(4, 2))
        if (m === 0 || m > 12) { return false }
        var monthLength = [ 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31 ]
        // Adjust for leap years
        if (y % 400 === 0 || (y % 100 !== 0 && y % 4 === 0)) { monthLength[1] = 29 }
        return d > 0 && d <= monthLength[m - 1]
      },
      citizenshipChange (citizenshipObj) {
        if (citizenshipObj.value !== 'MY') {
          let ethnicityExist = this.ethnicities.findIndex(ethnicity => ethnicity.lookup_uid === citizenshipObj.lookup_uid)
          let bornPlaceExist = this.placeOfBirths.findIndex(place => place.lookup_uid === citizenshipObj.lookup_uid)
          if (ethnicityExist === -1) {
            this.ethnicities.push(citizenshipObj)
          }
          if (bornPlaceExist === -1) {
            this.placeOfBirths.push(citizenshipObj)
          }
          this.localPersonalDetail.person.identity_card.ic_number = null
        }
      },      
    },
  }
</script>

<style scoped>
.font-18{
  font-size: 18px;
}

.font-calibri{
  font-family: Calibri;
}

.required >>> fieldset {
  border: 2px solid red;
}

.label-position >>> .v-label.v-label--active{
  margin-left:-28px;
  background:white;
  padding-right:5px
}
</style>
